import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Radar, LogOut, Download, Search, RefreshCw, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AdminLogin from "@/pages/AdminLogin";
import StatCards from "@/components/admin/StatCards";
import LeadsTable from "@/components/admin/LeadsTable";
import LeadDetail from "@/components/admin/LeadDetail";
import { STATUS_META } from "@/components/admin/StatusBadge";

const FUNNEL_LABELS = {
  domain_submitted: "Tests Run",
  results_viewed: "Results Viewed",
  phone_submitted: "Phone Captured",
  report_sent: "Reports Sent",
};

const Sidebar = ({ user, onLogout }) => (
  <aside className="md:w-60 bg-obsidian text-white md:min-h-screen p-6 flex md:flex-col justify-between items-center md:items-stretch">
    <div className="flex items-center gap-2">
      <div className="bg-white/10 h-9 w-9 flex items-center justify-center">
        <Radar className="h-5 w-5 text-conversion" />
      </div>
      <div className="hidden md:block">
        <div className="font-display text-lg">Dev<span className="text-conversion">.ai</span></div>
        <div className="font-data text-[10px] text-neutral-500 uppercase tracking-widest">CRM</div>
      </div>
    </div>
    <div className="md:mt-auto flex md:flex-col items-center md:items-start gap-3">
      <span className="font-data text-xs text-neutral-500 hidden md:block">{user?.email}</span>
      <button onClick={onLogout} className="flex items-center gap-2 font-data text-xs uppercase tracking-widest text-neutral-300 hover:text-conversion transition-colors" data-testid="logout-btn">
        <LogOut className="h-4 w-4" /> Logout
      </button>
    </div>
  </aside>
);

const FunnelChart = ({ funnelData }) => (
  <div className="bg-white border-2 border-[rgba(10,10,10,0.1)] p-6">
    <span className="av-label">Funnel Drop-off</span>
    <div className="h-[220px] w-full min-w-0 mt-4">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={funnelData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#525252" }} axisLine={{ stroke: "#0A0A0A" }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#525252" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Bar dataKey="value" radius={0} barSize={48}>
            {funnelData.map((entry, i) => (
              <Cell key={entry.label} fill={i === funnelData.length - 1 ? "#FF4500" : "#0A0A0A"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const FilterChip = ({ active, onClick, label, count, testId }) => (
  <button
    onClick={onClick}
    className={`font-data text-xs uppercase tracking-wider px-3 py-2 border-2 transition-colors ${active ? "bg-obsidian text-white border-obsidian" : "bg-white text-obsidian border-[rgba(10,10,10,0.15)] hover:border-obsidian"}`}
    data-testid={testId}
  >
    {label}{typeof count === "number" ? ` (${count})` : ""}
  </button>
);

function Dashboard() {
  const { logout, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    const res = await api.leads(params);
    setLeads(res.leads);
  }, [statusFilter, appliedSearch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s] = await Promise.all([api.stats(), loadLeads()]);
      setStats(s);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [loadLeads]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const funnelData = stats
    ? Object.entries(FUNNEL_LABELS).map(([k, label]) => ({ label, value: stats.funnel[k] || 0 }))
    : [];

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex flex-col md:flex-row">
      <Sidebar user={user} onLogout={logout} />

      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-obsidian">Lead Dashboard</h1>
            <p className="text-neutral-500 text-sm mt-1">Funnel analytics &amp; lead management</p>
          </div>
          <div className="flex gap-2">
            <button onClick={loadAll} className="av-btn-outline !px-4 !py-2.5 text-sm" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <a href={api.exportUrl} className="av-btn !px-4 !py-2.5 text-sm" data-testid="export-btn">
              <Download className="h-4 w-4" /> Export
            </a>
          </div>
        </div>

        {loading && !stats ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-conversion" /></div>
        ) : (
          <div className="space-y-6">
            <StatCards stats={stats} />
            <FunnelChart funnelData={funnelData} />

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <form
                onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search); }}
                className="flex items-center border-2 border-obsidian bg-white flex-1 max-w-md"
              >
                <Search className="h-4 w-4 text-neutral-400 ml-3" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search domain, brand or phone…"
                  className="flex-1 px-3 py-2.5 font-data text-sm focus:outline-none"
                  data-testid="lead-search"
                />
                <button type="submit" className="bg-obsidian text-white px-4 py-2.5 font-data text-xs uppercase">Go</button>
              </form>
              <div className="flex flex-wrap gap-2">
                <FilterChip active={statusFilter === ""} onClick={() => setStatusFilter("")} label="All" testId="filter-all" />
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <FilterChip key={k} active={statusFilter === k} onClick={() => setStatusFilter(k)} label={v.label} count={stats?.by_status?.[k]} testId={`filter-${k}`} />
                ))}
              </div>
            </div>

            <LeadsTable leads={leads} onSelect={setSelected} />
          </div>
        )}
      </main>

      <LeadDetail
        leadId={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onStatusChange={loadAll}
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]"><Loader2 className="h-8 w-8 animate-spin text-conversion" /></div>;
  }
  if (!user) return <AdminLogin />;
  return <Dashboard />;
}
